import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, BACKEND_URL } from 'apiConfig';
import type {
  User,
  LoginResponse,
  Preferences,
  PreferenceCreateRequest,
  Article,
  ArticleDetail,
  ArticleSearchRequest,
  ArticleSearchResponse,
  Digest,
  DigestListResponse,
  LibraryResponse,
  LibrarySaveRequest,
  LibrarySaveResponse,
  LitPortalHandoffPayload,
  LitPortalHandoffRequest,
  LitPortalHandoffResponse,
  FolderListResponse,
  FeedbackRequest,
  Note,
  NoteCreateRequest,
  NoteUpdateRequest,
  ReadingProgress,
  TopicsDashboard,
  SpecialtyConfig,
  AdminMetrics,
  SchedulerStatus,
  DiscussionThread,
  DiscussionComment,
  ThreadCreateRequest,
  CommentCreateRequest,
  CommentUpdateRequest,
  ThreadListResponse,
  ThreadDetailResponse,
  SpecialtyRoomListResponse,
  AttachableArticlesResponse,
  ContextType,
  ReactionType,
  VerificationStatusResponse,
  WorkEmailSendCodeRequest,
  WorkEmailCodeResponse,
  WorkEmailConfirmRequest,
  WorkEmailConfirmResponse,
  SubscriptionLevelResponse,
  NotificationListResponse,
  UnreadCountResponse,
  MarkReadRequest,
  MarkReadResponse,
} from '../types';

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ============================================================
// AUTH API
// ============================================================

export const authAPI = {
  login: (email: string, password: string): Promise<LoginResponse> =>
    apiClient.post('/auth/login', { email, password }).then((res) => res.data),

  signup: (data: { email: string; password: string; full_name?: string; specialty?: string }): Promise<User> =>
    apiClient.post('/auth/signup', data).then((res) => res.data),

  me: (): Promise<User> =>
    apiClient.get('/auth/me').then((res) => res.data),

  logout: (): Promise<{ message: string }> =>
    apiClient.post('/auth/logout').then((res) => res.data),

  verifyEmail: (token: string): Promise<{ message: string }> =>
    apiClient.post('/auth/verify-email', { token }).then((res) => res.data),

  resendVerification: (): Promise<{ message: string }> =>
    apiClient.post('/auth/resend-verification').then((res) => res.data),

  requestPasswordReset: (email: string): Promise<{ message: string }> =>
    apiClient.post('/auth/request-password-reset', { email }).then((res) => res.data),

  resetPassword: (token: string, new_password: string): Promise<{ message: string }> =>
    apiClient.post('/auth/reset-password', { token, new_password }).then((res) => res.data),
};

// ============================================================
// CONFIG API
// ============================================================

export const configAPI = {
  getSpecialties: (): Promise<SpecialtyConfig> =>
    apiClient.get('/config/specialties').then((res) => res.data),
};

// ============================================================
// PREFERENCES API
// ============================================================

export const preferencesAPI = {
  getMyPreferences: (): Promise<Preferences> =>
    apiClient.get('/preferences/me').then((res) => res.data),

  createOrUpdatePreferences: (data: PreferenceCreateRequest): Promise<Preferences> =>
    apiClient.post('/preferences', data).then((res) => res.data),

  // Phase 5: Digest Profiles
  listProfiles: (): Promise<any> =>
    apiClient.get('/preferences/profiles').then((res) => res.data),

  createProfile: (data: {
    name: string;
    specialty_id: string;
    subspecialty_id?: string | null;
    custom_keywords?: string[];
    schedule?: { frequency: string; hour?: number; minute?: number };
  }): Promise<any> =>
    apiClient.post('/preferences/profiles', data).then((res) => res.data),

  updateProfile: (profileId: string, data: Partial<{
    name: string;
    specialty_id: string;
    subspecialty_id: string | null;
    custom_keywords: string[];
    schedule: { frequency: string; hour?: number; minute?: number };
    is_active: boolean;
  }>): Promise<any> =>
    apiClient.put(`/preferences/profiles/${profileId}`, data).then((res) => res.data),

  deleteProfile: (profileId: string): Promise<any> =>
    apiClient.delete(`/preferences/profiles/${profileId}`).then((res) => res.data),

  setActiveProfiles: (activeProfileIds: string[]): Promise<any> =>
    apiClient.post('/preferences/profiles/set-active', { active_profile_ids: activeProfileIds }).then((res) => res.data),

  setPrimaryProfile: (profileId: string): Promise<any> =>
    apiClient.post(`/preferences/profiles/${profileId}/set-primary`).then((res) => res.data),

  getProfilesScreenStats: (): Promise<{
    profiles: Array<{
      profile_id: string;
      name: string;
      specialty_id: string;
      specialty_label: string;
      subspecialty_id: string;
      subspecialty_label: string;
      is_primary: boolean;
      last_digest_date: string | null;
      last_digest_id: string | null;
      unscreened_count: number;
      total_articles: number;
      saved_percent: number;
    }>;
    total_unscreened: number;
  }> =>
    apiClient.get('/preferences/profiles/screen-stats').then((res) => res.data),
};

// ============================================================
// DIGESTS API
// ============================================================

export const digestsAPI = {
  runDigestNow: async (sendEmail = false, profileId?: string): Promise<{ message: string; digest_id?: string; article_count?: number; status?: string }> => {
    try {
      const response = await apiClient.post('/digests/run-now', { send_email: sendEmail, profile_id: profileId }, { timeout: 180000 });
      return response.data;
    } catch (error: any) {
      // If timeout or network error, check if digest was actually created
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout') || error.message?.includes('Network Error')) {
        // Wait a moment then check for recent digests
        await new Promise(resolve => setTimeout(resolve, 2000));
        try {
          const digestsResponse = await apiClient.get('/digests?limit=1');
          const recentDigest = digestsResponse.data?.digests?.[0];
          if (recentDigest) {
            const digestDate = new Date(recentDigest.generated_at);
            const now = new Date();
            // If digest was created within last 5 minutes, consider it successful
            if ((now.getTime() - digestDate.getTime()) < 5 * 60 * 1000) {
              return {
                message: 'Digest generated successfully (confirmed after timeout)',
                digest_id: recentDigest.digest_id,
                article_count: recentDigest.article_count,
                status: 'completed'
              };
            }
          }
        } catch {
          // Ignore check error
        }
      }
      throw error;
    }
  },

  getMyDigests: (limit = 10): Promise<DigestListResponse> =>
    apiClient.get(`/digests?limit=${limit}`).then((res) => res.data),

  getDigestDetails: (digestId: string): Promise<Digest> =>
    apiClient.get(`/digests/${digestId}`).then((res) => res.data),
};

// ============================================================
// LIBRARY API
// ============================================================

export const libraryAPI = {
  getLibrary: (params?: {
    limit?: number;
    cursor?: string;
    search?: string;
    design_type?: string;
    saved_after?: string;
    sort_by?: string;
    sort_dir?: string;
  }): Promise<LibraryResponse> =>
    apiClient.get('/library', { params }).then((res) => res.data),

  saveArticle: (
    articleOrPmid: string | LibrarySaveRequest,
    folder: string = 'Inbox'
  ): Promise<LibrarySaveResponse> => {
    const body: LibrarySaveRequest =
      typeof articleOrPmid === 'string'
        ? { pmid: articleOrPmid, folder }
        : { folder: 'Inbox', ...articleOrPmid };

    return apiClient.post('/library/save', body).then((res) => res.data);
  },

  removeArticle: (pmid: string): Promise<{ message: string }> =>
    apiClient.delete(`/library/remove/${pmid}`).then((res) => res.data),

  submitFeedback: (pmid: string, feedback: string): Promise<{ message: string }> =>
    apiClient.post('/library/feedback', { pmid, feedback }).then((res) => res.data),

  getFolders: (): Promise<{ folders: Array<{ folder: string; type: string; count: number; subfolders?: any[]; profile_id?: string; specialty_id?: string }>; total_count: number }> =>
    apiClient.get('/library/folders').then((res) => res.data),

  createFolder: (name: string, parentFolderId?: string): Promise<{ folder_id: string; name: string; message: string }> =>
    apiClient.post('/library/folders/create', null, { params: { name, parent_folder_id: parentFolderId } }).then((res) => res.data),

  moveToFolder: (article_id: string, folder: string | null): Promise<{ message: string }> =>
    apiClient.post('/library/move', { article_id, folder }).then((res) => res.data),

  // Phase A v2: Topics Dashboard
  getTopicsSummary: (): Promise<TopicsDashboard> =>
    apiClient.get('/library/topics-summary').then((res) => res.data),

  // Step 4: Export (premium-only, returns file blob)
  exportLibrary: async (format: 'csv' | 'ris'): Promise<void> => {
    const response = await apiClient.get(`/library/export?format=${format}`, { responseType: 'blob' });
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const ext = format === 'csv' ? 'csv' : 'ris';
    a.download = `litpulse_library_${new Date().toISOString().slice(0, 10)}.${ext}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

// ============================================================
// LITPORTAL HANDOFF API (LitCurrent-owned)
// ============================================================

export const litPortalAPI = {
  createHandoff: (data: LitPortalHandoffRequest): Promise<LitPortalHandoffResponse> =>
    apiClient.post('/litportal/handoff', data).then((res) => res.data),

  consumeHandoff: (token: string): Promise<LitPortalHandoffPayload> =>
    apiClient.get(`/litportal/handoff/${encodeURIComponent(token)}`).then((res) => res.data),
};

// ============================================================
// ARTICLES API
// ============================================================

export const articlesAPI = {
  search: (query: string, filters: Partial<ArticleSearchRequest> = {}): Promise<ArticleSearchResponse> =>
    apiClient.post('/articles/search', { query, ...filters }).then((res) => res.data),

  // Phase A v2: Article Detail
  getDetail: (articleId: string): Promise<ArticleDetail> =>
    apiClient.get(`/articles/${articleId}`).then((res) => res.data),

  // Step 5: Audio Takeaway
  getAudioSummary: (pmid: string): Promise<any> =>
    apiClient.get(`/articles/${pmid}/audio-summary`).then((res) => res.data),

  generateAudio: (pmid: string): Promise<any> =>
    apiClient.post(`/articles/${pmid}/audio-summary/generate`).then((res) => res.data),

  retryAudio: (pmid: string): Promise<any> =>
    apiClient.post(`/articles/${pmid}/audio-summary/retry`).then((res) => res.data),

  // My Audio Summaries — individual article audio history
  getMyAudioSummaries: (): Promise<any> =>
    apiClient.get('/audio/my-summaries').then((res) => res.data),

  // Phase 4: Explore V2
  searchV2: (params: {
    query: string;
    use_preferences_context?: boolean;
    study_types?: string[];
    lookback_years?: number;
    limit?: number;
  }): Promise<any> =>
    apiClient.post('/articles/search-v2', params).then((res) => res.data),

  topicSuggest: (q: string): Promise<{ suggestions: string[] }> =>
    apiClient.get('/articles/v2/suggest', { params: { q } }).then((res) => res.data),
};

// ============================================================
// PLAYLIST API (Step 5)
// ============================================================

export const playlistAPI = {
  getDigestPlaylist: (digestId: string, autoGenerate = false): Promise<any> =>
    apiClient.get(`/playlists/digest/${digestId}`, { params: { auto_generate_missing: autoGenerate } }).then((res) => res.data),

  getFolderPlaylist: (folderName: string, autoGenerate = false): Promise<any> =>
    apiClient.get(`/playlists/folder/${folderName}`, { params: { auto_generate_missing: autoGenerate } }).then((res) => res.data),
};

// ============================================================
// BRIEFINGS API (Step 5)
// ============================================================

export const briefingsAPI = {
  getLatest: (): Promise<any> =>
    apiClient.get('/briefings/latest').then((res) => res.data),
};

// ============================================================
// COPILOT API (Step 12) — kept for backward compatibility
// ============================================================

export const copilotAPI = {
  evidenceBrief: (pmid: string): Promise<any> =>
    apiClient.post('/copilot/evidence-brief', { pmid }).then((res) => res.data),

  askArticle: (pmid: string | string[], question: string): Promise<any> =>
    apiClient
      .post('/copilot/ask-article', {
        pmid: Array.isArray(pmid) ? pmid : [pmid],
        question,
      })
      .then((res) => res.data),

  compareStudies: (pmids: string[], question?: string): Promise<any> =>
    apiClient.post('/copilot/compare-studies', { pmids, question_optional: question || null }).then((res) => res.data),

  draftPost: (data: { specialty_id: string; pmids: string[]; tone?: string; thread_title_optional?: string; prompt_optional?: string }): Promise<any> =>
    apiClient.post('/copilot/draft-discussion-post', data).then((res) => res.data),
};

// ============================================================
// LIT SCHOLAR API — UI rebrand wrapper over copilot endpoints
// ============================================================

export const litScholarAPI = {
  evidenceBrief: (pmid: string, useExpertise = true): Promise<any> =>
    apiClient.post('/copilot/evidence-brief', { pmid, use_expertise_context: useExpertise }).then((res) => res.data),

  askArticle: (pmid: string | string[], question: string, useExpertise = true): Promise<any> =>
    apiClient
      .post('/copilot/ask-article', {
        pmid: Array.isArray(pmid) ? pmid : [pmid],
        question,
        use_expertise_context: useExpertise,
      })
      .then((res) => res.data),

  compareStudies: (pmids: string[], question?: string, useExpertise = true): Promise<any> =>
    apiClient.post('/copilot/compare-studies', { pmids, question_optional: question || null, use_expertise_context: useExpertise }).then((res) => res.data),

  draftPost: (data: { specialty_id: string; pmids: string[]; tone?: string; thread_title_optional?: string; prompt_optional?: string; use_expertise_context?: boolean }): Promise<any> =>
    apiClient.post('/copilot/draft-discussion-post', { use_expertise_context: true, ...data }).then((res) => res.data),

  // Profile & artifacts
  getProfile: (): Promise<any> =>
    apiClient.get('/litscholar/profile').then((res) => res.data),

  rebuildProfile: (): Promise<any> =>
    apiClient.post('/litscholar/profile/rebuild').then((res) => res.data),

  getArtifacts: (limit = 20): Promise<any> =>
    apiClient.get('/litscholar/artifacts', { params: { limit } }).then((res) => res.data),

  saveArtifact: (data: { artifact_type: string; title: string; summary_text: string; pmids: string[] }): Promise<any> =>
    apiClient.post('/litscholar/artifacts', data).then((res) => res.data),

  deleteArtifact: (artifactId: string): Promise<any> =>
    apiClient.delete(`/litscholar/artifacts/${artifactId}`).then((res) => res.data),
};

// ============================================================
// NOTES API (Phase A v2)
// ============================================================

export const notesAPI = {
  getNotesForArticle: (articleId: string): Promise<Note[]> =>
    apiClient.get(`/notes?article_id=${articleId}`).then((res) => res.data),

  createNote: (articleId: string, body: string): Promise<Note> =>
    apiClient.post('/notes', { article_id: articleId, body }).then((res) => res.data),

  updateNote: (noteId: string, body: string): Promise<Note> =>
    apiClient.put(`/notes/${noteId}`, { body }).then((res) => res.data),

  deleteNote: (noteId: string): Promise<void> =>
    apiClient.delete(`/notes/${noteId}`).then(() => undefined),
};

// ============================================================
// ARTICLE METADATA API — Tags & Collections (sidecar)
// ============================================================

export const articleMetadataAPI = {
  // Tags
  getTags: (pmid: string): Promise<{ pmid: string; tags: string[] }> =>
    apiClient.get(`/article-metadata/tags/${pmid}`).then((res) => res.data),

  setTags: (pmid: string, tags: string[]): Promise<{ pmid: string; tags: string[] }> =>
    apiClient.put('/article-metadata/tags', { pmid, tags }).then((res) => res.data),

  getAllTags: (): Promise<Array<{ pmid: string; tags: string[] }>> =>
    apiClient.get('/article-metadata/tags').then((res) => res.data),

  // Collections
  listCollections: (): Promise<Array<{ collection_id: string; name: string; article_count: number; created_at: string }>> =>
    apiClient.get('/article-metadata/collections').then((res) => res.data),

  createCollection: (name: string): Promise<{ collection_id: string; name: string }> =>
    apiClient.post('/article-metadata/collections', { name }).then((res) => res.data),

  renameCollection: (collectionId: string, name: string): Promise<void> =>
    apiClient.put(`/article-metadata/collections/${collectionId}`, { name }).then(() => undefined),

  deleteCollection: (collectionId: string): Promise<void> =>
    apiClient.delete(`/article-metadata/collections/${collectionId}`).then(() => undefined),

  assignToCollection: (pmid: string, collectionId: string): Promise<void> =>
    apiClient.post('/article-metadata/collections/assign', { pmid, collection_id: collectionId }).then(() => undefined),

  unassignFromCollection: (collectionId: string, pmid: string): Promise<void> =>
    apiClient.delete(`/article-metadata/collections/assign/${collectionId}/${pmid}`).then(() => undefined),

  getArticleCollections: (pmid: string): Promise<{ pmid: string; collection_ids: string[] }> =>
    apiClient.get(`/article-metadata/collections/article/${pmid}`).then((res) => res.data),
};

// ============================================================
// READING TRACKING API (Phase A v2)
// ============================================================

export const readingAPI = {
  recordOpened: (articleId: string): Promise<{ message: string; article_id: string }> =>
    apiClient.post('/reading/opened', { article_id: articleId }).then((res) => res.data),

  markRead: (articleId: string, isRead = true): Promise<{ message: string; article_id: string; is_read: boolean }> =>
    apiClient.post('/reading/mark-read', { article_id: articleId, is_read: isRead }).then((res) => res.data),

  getProgress: (): Promise<ReadingProgress> =>
    apiClient.get('/reading/progress').then((res) => res.data),
};

// ============================================================
// DISCUSSIONS API (Phase B - Community)
// ============================================================

export const discussionsAPI = {
  // Thread operations
  createThread: (data: ThreadCreateRequest): Promise<DiscussionThread> =>
    apiClient.post('/discussions/threads', data).then((res) => res.data),

  getThreads: (contextType: ContextType, contextId: string): Promise<ThreadListResponse> =>
    apiClient.get(`/discussions/threads?context_type=${contextType}&context_id=${contextId}`).then((res) => res.data),

  getThreadDetail: (threadId: string): Promise<ThreadDetailResponse> =>
    apiClient.get(`/discussions/threads/${threadId}`).then((res) => res.data),

  // Comment operations
  createComment: (threadId: string, data: CommentCreateRequest): Promise<DiscussionComment> =>
    apiClient.post(`/discussions/threads/${threadId}/comments`, data).then((res) => res.data),

  updateComment: (commentId: string, data: CommentUpdateRequest): Promise<DiscussionComment> =>
    apiClient.patch(`/discussions/comments/${commentId}`, data).then((res) => res.data),

  deleteComment: (commentId: string): Promise<void> =>
    apiClient.delete(`/discussions/comments/${commentId}`).then(() => undefined),

  // Reactions
  addReaction: (commentId: string, reactionType: ReactionType): Promise<DiscussionComment> =>
    apiClient.post(`/discussions/comments/${commentId}/react`, { reaction_type: reactionType }).then((res) => res.data),

  // Specialty rooms
  getSpecialtyRooms: (): Promise<SpecialtyRoomListResponse> =>
    apiClient.get('/discussions/specialty-rooms').then((res) => res.data),

  getSpecialtyThreads: (specialtyId: string, primaryArticlePmid?: string): Promise<ThreadListResponse> => {
    const params = primaryArticlePmid ? { primary_article_pmid: primaryArticlePmid } : {};
    return apiClient.get(`/discussions/specialties/${specialtyId}`, { params }).then((res) => res.data);
  },

  // Attachable articles
  getAttachableArticles: (): Promise<AttachableArticlesResponse> =>
    apiClient.get('/discussions/attachable-articles').then((res) => res.data),

  // Report
  reportComment: (commentId: string, reason: string, reason_category?: string): Promise<{ message: string }> =>
    apiClient.post(`/discussions/comments/${commentId}/report`, { reason, reason_category: reason_category || 'other' }).then((res) => res.data),
};

// ============================================================
// ADMIN API
// ============================================================

export const adminAPI = {
  getMetrics: (): Promise<AdminMetrics> =>
    apiClient.get('/admin/metrics').then((res) => res.data),

  getSchedulerStatus: (): Promise<SchedulerStatus> =>
    apiClient.get('/admin/scheduler-status').then((res) => res.data),

  // Moderation (Step 3)
  getReports: (params?: { status?: string; reason?: string; limit?: number }): Promise<any> =>
    apiClient.get('/admin/reports', { params }).then((res) => res.data),

  getReportDetail: (reportId: string): Promise<any> =>
    apiClient.get(`/admin/reports/${reportId}`).then((res) => res.data),

  resolveReport: (reportId: string, data: { resolution_note: string }): Promise<any> =>
    apiClient.post(`/admin/reports/${reportId}/resolve`, data).then((res) => res.data),

  removeComment: (data: { comment_id: string; reason: string }): Promise<any> =>
    apiClient.post('/admin/moderation/remove-comment', data).then((res) => res.data),

  suspendUser: (data: { user_id: string; reason: string }): Promise<any> =>
    apiClient.post('/admin/moderation/suspend-user', data).then((res) => res.data),

  unsuspendUser: (data: { user_id: string }): Promise<any> =>
    apiClient.post('/admin/moderation/unsuspend-user', data).then((res) => res.data),

  getComment: (commentId: string, includeBody?: boolean): Promise<any> =>
    apiClient.get(`/admin/comments/${commentId}`, { params: { include_body: includeBody } }).then((res) => res.data),

  // Go-Live Readiness (Step 11)
  getGoLiveStatus: (): Promise<any> =>
    apiClient.get('/admin/go-live-status').then((res) => res.data),

  runLiveChecks: (): Promise<any> =>
    apiClient.post('/admin/go-live-status/run-live-checks').then((res) => res.data),

  // Slow Queries (Step 17)
  getSlowQueries: (limit = 50): Promise<any> =>
    apiClient.get('/admin/slow-queries', { params: { limit } }).then((res) => res.data),
};

// ============================================================
// BILLING API (Step 4)
// ============================================================

export const billingAPI = {
  createCheckout: (originUrl: string): Promise<{ url: string; session_id: string }> =>
    apiClient.post('/billing/stripe/checkout-session', { origin_url: originUrl }).then((res) => res.data),

  createPortalSession: (originUrl: string): Promise<{ url: string }> =>
    apiClient.post('/billing/stripe/portal-session', { origin_url: originUrl }).then((res) => res.data),

  getCheckoutStatus: (sessionId: string): Promise<any> =>
    apiClient.get(`/billing/checkout/status/${sessionId}`).then((res) => res.data),

  getBillingStatus: (): Promise<any> =>
    apiClient.get('/billing/me').then((res) => res.data),

  // Phase 2: Start 30-day Pro trial (no Stripe required)
  startTrial: (): Promise<{
    trial_started: boolean;
    trial_started_at: string;
    trial_expires_at: string;
    days_remaining: number;
    capabilities: Record<string, unknown>;
  }> => apiClient.post('/billing/start-trial').then((res) => res.data),
};

// ============================================================
// AUDIO DIGESTS V2 API (Phase 7)
// ============================================================

export const audioDigestsAPI = {
  create: (params: {
    pmids: string[];
    title?: string;
    auto_generate_missing?: boolean;
    mode?: 'playlist' | 'combined_summary';
  }): Promise<any> =>
    apiClient.post('/audio-digests', params).then((res) => res.data),

  list: (): Promise<any> =>
    apiClient.get('/audio-digests').then((res) => res.data),

  get: (audioDigestId: string): Promise<any> =>
    apiClient.get(`/audio-digests/${audioDigestId}`).then((res) => res.data),

  downloadZipUrl: (audioDigestId: string): string =>
    `${BACKEND_URL}/api/audio-digests/${audioDigestId}/download.zip`,

  delete: (audioDigestId: string): Promise<any> =>
    apiClient.delete(`/audio-digests/${audioDigestId}`).then((res) => res.data),
};

// ============================================================
// VERIFICATION API (v2.1)
// ============================================================

export const verificationAPI = {
  // Get verification status
  getStatus: (): Promise<VerificationStatusResponse> =>
    apiClient.get('/verification/me').then((res) => res.data),

  // Get subscription level
  getSubscriptionLevel: (): Promise<SubscriptionLevelResponse> =>
    apiClient.get('/verification/subscription-level').then((res) => res.data),

  // Send verification code to work email
  sendWorkEmailCode: (data: WorkEmailSendCodeRequest): Promise<WorkEmailCodeResponse> =>
    apiClient.post('/verification/work-email/send-code', data).then((res) => res.data),

  // Confirm verification code
  confirmWorkEmailCode: (data: WorkEmailConfirmRequest): Promise<WorkEmailConfirmResponse> =>
    apiClient.post('/verification/work-email/confirm', data).then((res) => res.data),

  // Submit NPI for verification
  submitNPI: (npi_number: string): Promise<{ status: string; message: string; method: string }> =>
    apiClient.post('/verification/npi/submit', { npi_number }).then((res) => res.data),
};

// ============================================================
// NOTIFICATIONS API (v2.1)
// ============================================================

export const notificationsAPI = {
  // Get unread count
  getUnreadCount: (): Promise<UnreadCountResponse> =>
    apiClient.get('/notifications/unread-count').then((res) => res.data),

  // Get notifications list
  getNotifications: (params?: { limit?: number; cursor?: string; unread_only?: boolean }): Promise<NotificationListResponse> =>
    apiClient.get('/notifications/', { params }).then((res) => res.data),

  // Mark notifications as read
  markRead: (data: MarkReadRequest): Promise<MarkReadResponse> =>
    apiClient.post('/notifications/mark-read', data).then((res) => res.data),

  // Mark thread notifications as read
  markThreadRead: (threadId: string): Promise<MarkReadResponse> =>
    apiClient.post(`/notifications/mark-thread-read?thread_id=${threadId}`).then((res) => res.data),
};

// ============================================================
// WORKSPACE API (Workspace Shell V1)
// ============================================================

export interface WorkspaceBootstrap {
  has_preferences: boolean;
  unscreened_count: number;
  latest_digest_id: string | null;
  has_library_items: boolean;
  library_count: number;
  recent_articles: Array<{ pmid: string; title: string; saved_at: string | null }>;
  recent_digests: Array<{ digest_id: string; generated_at: string | null; article_count: number }>;
}

export interface ScreeningDecision {
  article_id: string;
  digest_id?: string;
  decision: 'keep' | 'later' | 'skip';
}

export interface ScreeningDecisionResponse {
  success: boolean;
  message: string;
  saved_to_library: boolean;
}

export interface PendingScreeningResponse {
  articles: Article[];
  total_pending: number;
  digest_id: string | null;
}

// Screen Queue Types (Step 3)
export interface DigestSession {
  digest_id: string;
  generated_at: string | null;
  article_count: number;
  unscreened_count: number;
  saved_count: number;
  deferred_count: number;
  skipped_count: number;
  is_latest: boolean;
}

export interface ScreenProgress {
  total: number;
  saved: number;
  deferred: number;
  skipped: number;
  remaining: number;
}

export interface ScreenQueueArticle {
  pmid: string;
  title: string;
  abstract?: string;
  journal?: string;
  authors?: string;
  pub_date?: string;
  ai_summary?: string;
  design_tags?: string[];
  topic_tags?: string[];
  digest_id?: string;
  screening_status?: 'keep' | 'later' | 'skip' | null;
  is_in_library?: boolean;
  needs_ai_summary?: boolean;
}

export interface ScreenQueueResponse {
  articles: ScreenQueueArticle[];
  progress: ScreenProgress;
  digest_id: string | null;
  digest_date: string | null;
  filter_status: string;
}

export interface ScreenDigestListResponse {
  digests: DigestSession[];
  latest_digest_id: string | null;
}

export const workspaceAPI = {
  // Get workspace bootstrap data for smart routing
  getBootstrap: (): Promise<WorkspaceBootstrap> =>
    apiClient.get('/workspace/bootstrap').then((res) => res.data),

  // Record a screening decision
  recordScreeningDecision: (decision: ScreeningDecision): Promise<ScreeningDecisionResponse> =>
    apiClient.post('/workspace/screening/decision', decision).then((res) => res.data),

  // Get pending articles for screening (legacy)
  getPendingScreening: (params?: { digest_id?: string; limit?: number }): Promise<PendingScreeningResponse> =>
    apiClient.get('/workspace/screening/pending', { params }).then((res) => res.data),

  // Step 3: Screen Queue Endpoints
  // Get list of digests with progress stats
  getScreenDigests: (params?: { limit?: number }): Promise<ScreenDigestListResponse> =>
    apiClient.get('/workspace/screen/digests', { params }).then((res) => res.data),

  // Get screening queue with filtering
  getScreenQueue: (params?: { 
    digest_id?: string; 
    status?: 'unscreened' | 'deferred' | 'saved' | 'all';
    limit?: number;
  }): Promise<ScreenQueueResponse> =>
    apiClient.get('/workspace/screen/queue', { params }).then((res) => res.data),

  // Trigger AI summary regeneration for articles with missing summaries
  // Non-blocking: fire-and-forget from the Screen flow
  regenerateSummaries: (pmids: string[]): Promise<{ status: string; regenerating: number }> =>
    apiClient.post('/workspace/screen/regenerate-summaries', { pmids }).then((res) => res.data),
};

// ============================================================================
// RAG API (Full-Text Analysis)
// ============================================================================
export interface RAGUploadResponse {
  success: boolean;
  document_id: string;
  filename: string;
  total_pages: number;
  total_chunks: number;
  message: string;
}

export interface RAGQueryResponse {
  answer: string;
  sources: Array<{
    page: number;
    section: number;
    relevance: number;
    excerpt: string;
  }>;
  document_id: string;
}

export interface RAGDocumentInfo {
  document_id: string;
  pmid?: string;
  filename: string;
  total_pages: number;
  total_chunks: number;
  created_at: string;
}

export const ragAPI = {
  // Upload and process a PDF for RAG analysis
  uploadPdf: async (file: File, pmid?: string): Promise<RAGUploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    if (pmid) {
      formData.append('pmid', pmid);
    }
    const response = await apiClient.post('/rag/upload-pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Query a processed document using RAG
  query: async (params: {
    document_id: string;
    query: string;
    article_title?: string;
    article_abstract?: string;
    k?: number;
  }): Promise<RAGQueryResponse> => {
    const response = await apiClient.post('/rag/query', params);
    return response.data;
  },

  // Get info about a processed document
  getDocumentInfo: async (documentId: string): Promise<RAGDocumentInfo> => {
    const response = await apiClient.get(`/rag/document/${documentId}`);
    return response.data;
  },

  // Delete a processed document
  deleteDocument: async (documentId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/rag/document/${documentId}`);
    return response.data;
  },

  // Search for relevant chunks (without generating answer)
  searchChunks: async (params: {
    document_id: string;
    query: string;
    k?: number;
  }): Promise<Array<{
    content: string;
    page: number;
    section: number;
    relevance: number;
  }>> => {
    const formData = new FormData();
    formData.append('document_id', params.document_id);
    formData.append('query', params.query);
    if (params.k) formData.append('k', params.k.toString());
    const response = await apiClient.post('/rag/search', formData);
    return response.data;
  },
};

export default apiClient;
